import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Layers, SkipForward } from "lucide-react";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill, Money, Empty } from "@/components/common";
import { ClassifyForm } from "@/components/ClassifyForm";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Attention() {
  const { accounts } = useApp();
  const nav = useNavigate();
  const [q, setQ] = useState({ items: [], groups: [] });
  const [idx, setIdx] = useState(0);
  const [other, setOther] = useState({ classified: 0, dups: 0, failed: [] });
  const [group, setGroup] = useState(null);
  const load = useCallback(async () => {
    const [a, b, c, s] = await Promise.all([api.get("/review-queue"), api.get("/transactions", { params: { status: "classified", limit: 1 } }),
      api.get("/transactions", { params: { dup_status: "possible_duplicate", limit: 1 } }), api.get("/statements")]);
    setQ(a.data);
    setOther({ classified: b.data.total, dups: c.data.total, failed: s.data.filter((x) => x.status === "failed") });
  }, []);
  useEffect(() => { load(); }, [load]);
  const t = q.items[Math.min(idx, q.items.length - 1)];
  const acct = t && accounts.find((a) => a.id === t.account_id);
  const suspense = async () => {
    try { await api.post("/transactions/bulk", { ids: [t.id], action: "suspense", note: "Unknown — parked from review queue" }); toast.success("Moved to suspense"); load(); }
    catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Needs My Attention" sub="Answer a few questions per entry. Nothing is learned until you confirm it. Uncertain entries can be parked in suspense — never silently cleared." />
      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <button onClick={() => nav("/transactions?status=classified")} className="panel p-4 text-left transition-colors hover:border-primary/50" data-testid="attention-awaiting-approval">
          <div className="label-xs">Awaiting checker approval</div><div className="num mt-1 text-2xl">{other.classified}</div></button>
        <button onClick={() => nav("/transactions?dup_status=possible_duplicate")} className="panel p-4 text-left transition-colors hover:border-primary/50" data-testid="attention-possible-dups">
          <div className="label-xs">Possible duplicates to decide</div><div className="num mt-1 text-2xl">{other.dups}</div></button>
        <button onClick={() => nav("/reconciliation")} className="panel p-4 text-left transition-colors hover:border-primary/50" data-testid="attention-failed-statements">
          <div className="label-xs">Statements failing checks</div><div className="num mt-1 text-2xl text-rose-500">{other.failed.length}</div></button>
      </div>
      {q.groups.length > 0 && (
        <div className="panel mb-6 p-5">
          <div className="mb-3 flex items-center gap-2 font-semibold"><Layers className="h-4 w-4 text-teal-500" />Bulk confirm identical counterparties</div>
          <div className="flex flex-wrap gap-2">
            {q.groups.map((g) => (
              <Button key={g.key + g.direction} variant="outline" size="sm" onClick={() => setGroup(g)} data-testid="bulk-group-button">
                {g.key} · {g.direction} · {g.ids.length}
              </Button>
            ))}
          </div>
        </div>
      )}
      {!t ? <Empty title="All caught up" sub="No transactions waiting for your answers." /> : (
        <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
          <div className="panel p-6 fade-up" key={t.id} data-testid="review-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="label-xs">{idx + 1} of {q.items.length} · {fmtDate(t.txn_date)} · {acct?.bank_name} {acct?.masked} · page {t.page} row {t.row_no}</div>
                <Money v={t.amount} dir={t.direction} className="mt-2 block text-3xl font-semibold" />
                <div className="text-xs text-muted-foreground">{t.direction === "credit" ? "Money received" : "Money paid"} · {t.mode}</div>
              </div>
              <div className="flex gap-2">
                <Pill s={t.dup_status} />
                {(t.tags || []).map((g) => <span key={g} className="rounded-full bg-accent px-2 text-[11px]">{g.replace("_", " ")}</span>)}
              </div>
            </div>
            <div className="mt-4 rounded-lg bg-muted p-3 font-mono text-sm" data-testid="review-narration">{t.narration}</div>
            {t.ai_suggestion?.reason && <div className="mt-2 text-xs text-muted-foreground">AI note: {t.ai_suggestion.reason}</div>}
            <div className="mt-6"><ClassifyForm key={t.id} txns={[t]} onDone={load} /></div>
            <div className="mt-4 flex gap-2 border-t pt-4">
              <Button variant="ghost" size="sm" onClick={() => setIdx((i) => (i + 1) % q.items.length)} data-testid="review-skip-button"><SkipForward className="mr-2 h-4 w-4" />Skip</Button>
              <Button variant="ghost" size="sm" onClick={suspense} data-testid="review-suspense-button">I don't know — send to suspense</Button>
            </div>
          </div>
          <div className="panel max-h-[80vh] overflow-auto p-2">
            {q.items.map((x, i) => (
              <button key={x.id} onClick={() => setIdx(i)} className={`block w-full rounded-lg px-3 py-2 text-left text-xs transition-colors hover:bg-accent ${x.id === t.id ? "row-active" : ""}`} data-testid="review-queue-item">
                <div className="flex justify-between"><span className="num text-muted-foreground">{fmtDate(x.txn_date)}</span><Money v={x.amount} dir={x.direction} /></div>
                <div className="truncate font-mono">{x.narration}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      <Dialog open={!!group} onOpenChange={(o) => !o && setGroup(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bulk confirm: {group?.key}</DialogTitle></DialogHeader>
          {group && <ClassifyForm txns={q.items.filter((x) => group.ids.includes(x.id))} onDone={() => { setGroup(null); load(); }} submitLabel={`Confirm ${group.ids.length} entries`} />}
          {group && <div className="text-xs text-muted-foreground">Total {inr(q.items.filter((x) => group.ids.includes(x.id)).reduce((a, x) => a + Number(x.amount), 0).toFixed(2))}</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
